import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronRight, Users, Heart } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { SignedImg } from "@/lib/media";
import { toast } from "sonner";
import { RelationshipPicker, OTHER, ROLES, RELATIONSHIPS, cleanLabel, labelError } from "@/components/RelationshipPicker";


export default function Welcome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [role, setRole] = useState(user?.family_label || "");
  const [roleOther, setRoleOther] = useState(false);
  const [photo, setPhoto] = useState(user?.photo || null);
  const [form, setForm] = useState({ name: "", relationship: "", birth_year: "" });
  const [saving, setSaving] = useState(false);
  const [demoing, setDemoing] = useState(false);
  const photoInput = useRef(null);

  useEffect(() => {
    api.get("/family").then((r) => {
      if ((r.data.members || []).length > 0) navigate("/dashboard", { replace: true });
    }).catch(() => {});
  }, [navigate]);

  const saveRole = (r, isOther) => {
    setRole(r); setRoleOther(isOther);
    if (!isOther) api.put("/profile", { family_label: r }).catch(() => {});
  };
  const saveCustomRole = () => {
    if (roleOther && !labelError(role)) api.put("/profile", { family_label: cleanLabel(role) }).catch(() => {});
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/profile/photo", fd); setPhoto(data.photo); }
    catch (err) { toast.error(apiError(err)); }
  };

  const addFirst = async () => {
    if (roleOther && labelError(role)) return toast.error(labelError(role));
    if (!form.name.trim() || !form.relationship) return toast.error("Please add a name and relationship.");
    const relErr = labelError(form.relationship, "rel");
    if (relErr) return toast.error(relErr);
    setSaving(true);
    try {
      if (role) await api.put("/profile", { family_label: cleanLabel(role) });
      const { data: m } = await api.post("/members", { ...form, relationship: cleanLabel(form.relationship) });
      navigate(`/member/${m.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const loadDemo = async () => {
    setDemoing(true);
    toast.info("Preparing the Sterling family archive — this takes a moment…");
    try { await api.post("/demo/seed"); navigate("/dashboard"); }
    catch (e) { toast.error(apiError(e)); setDemoing(false); }
  };

  const field = "w-full h-14 px-4 rounded-xl bg-[#1A1222] border border-[#33264A] text-[#EDE6F7] text-lg placeholder:text-[#8C84A0] focus:outline-none focus:border-[#8B2CFF]";
  const pill = (active) => `h-12 px-5 rounded-full border text-base transition-colors ${active ? "gold-btn" : "ghost-gold"}`;

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-10 fade-up">
          <div className="flex justify-center mb-5"><BrandMark size={48} /></div>
          <p className="eyebrow mb-2">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#EDE6F7] leading-tight">Let's begin your family story</h1>
          <p className="text-lg text-[#B9B3C8] mt-4 max-w-lg mx-auto">
            First, tell us who you are in the family. Then add one person you'd love to leave a memory for.
          </p>
        </div>

        {/* Step 1 — creator profile */}
        <div className="glass-card p-6 sm:p-8 mb-6 fade-up">
          <p className="eyebrow mb-3">Step 1 · You</p>
          <div className="flex items-center gap-5 mb-6">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-[#1A1222] border border-[#33264A] overflow-hidden flex items-center justify-center">
                {photo ? <SignedImg path={photo} alt="" className="h-full w-full object-cover" />
                       : <Heart className="h-8 w-8 text-[#5E5670]" />}
              </div>
              <button onClick={() => photoInput.current?.click()} data-testid="creator-photo-btn"
                className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full gold-btn flex items-center justify-center">
                <Camera className="h-4 w-4" />
              </button>
              <input ref={photoInput} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
            </div>
            <p className="text-[#B9B3C8]">Add a photo of yourself (optional).</p>
          </div>
          <label className="eyebrow block mb-3">What does your family call you?</label>
          <div data-testid="role-picker" onBlur={saveCustomRole}>
            <RelationshipPicker options={ROLES} value={role} onChange={saveRole} testid="role" pill={pill}
              inputLabel="What does your family call you?" placeholder="e.g. Auntie Jean, Papa John" />
          </div>
        </div>

        {/* Step 2 — first family member */}
        <div className="glass-card p-6 sm:p-8 fade-up" data-testid="welcome-first-member">
          <p className="eyebrow mb-3">Step 2 · Your first person</p>
          <h2 className="font-serif text-3xl text-[#EDE6F7] mb-5">Who would you like to leave a memory for?</h2>
          <div className="space-y-5">
            <div>
              <label className="eyebrow block mb-2">Their name</label>
              <input data-testid="welcome-name-input" className={field} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Freddie" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Relationship</label>
              <RelationshipPicker options={RELATIONSHIPS} value={form.relationship} testid="welcome-rel" pill={pill}
                onChange={(v) => setForm((f) => ({ ...f, relationship: v }))}
                inputLabel="What is their relationship to you?" errorKind="rel" placeholder="e.g. Niece, Godson, Stepdaughter" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Approximate year they were born (optional)</label>
              <input data-testid="welcome-year-input" className={field} value={form.birth_year}
                onChange={(e) => setForm({ ...form, birth_year: e.target.value })} placeholder="1998" />
            </div>
            <button data-testid="welcome-save-btn" onClick={addFirst} disabled={saving}
              className="gold-btn w-full h-16 rounded-full text-xl flex items-center justify-center disabled:opacity-60">
              {saving ? "Saving…" : <>Save &amp; Record a Memory <ChevronRight className="h-6 w-6 ml-1" /></>}
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 fade-up">
          <button data-testid="welcome-demo-btn" onClick={loadDemo} disabled={demoing}
            className="ghost-gold h-13 px-6 py-3 rounded-full text-lg flex items-center">
            <Users className="h-5 w-5 mr-2" /> {demoing ? "Loading demo…" : "Show me a demonstration family first"}
          </button>
          <button data-testid="welcome-skip-btn" onClick={() => navigate("/dashboard")}
            className="text-[#8C84A0] underline">I'll add family later</button>
        </div>
      </main>
    </div>
  );
}
